import { useEffect, useRef } from 'react';
import {
  WINE_COLORS,
  WINE_FIELD_LABELS,
  WINE_WORLDS,
  WINE_WORLD_LABELS,
  effectiveWineWorld,
  showsVarietal,
  structuredWineDisplay,
  type EventWorldScope,
} from '@gwc/shared';
import { WINE_COLOR_LABELS, isWineColor, type StructuredWineDraft } from '../api/rsvp';
import { LookupTypeahead } from './LookupTypeahead';

/**
 * The structured "what are you bringing?" fields. With a tasting that takes
 * both sides the member first says Old or New World, and the rest follows that
 * choice: Old World asks for the commune and never the grape.
 */
export function StructuredWineFields({
  value,
  onChange,
  worldScope,
  categoryColor,
  disabled,
  forSomeoneElse = false,
}: {
  value: StructuredWineDraft;
  onChange: (v: StructuredWineDraft) => void;
  worldScope: EventWorldScope;
  /** The picked category's colour hint, used to pre-fill the colour. */
  categoryColor?: string | null;
  disabled?: boolean;
  /** An admin entering another member's bottle: don't ask about "your" wine. */
  forSomeoneElse?: boolean;
}) {
  const world = effectiveWineWorld(worldScope, value.world);
  const set = (patch: StructuredWineDraft) => onChange({ ...value, ...patch });

  // Pre-fill the colour from the category: when there's none yet, and again
  // whenever the member switches category (Reds → Whites means white).
  const lastCategoryColor = useRef(categoryColor);
  useEffect(() => {
    const changed = lastCategoryColor.current !== categoryColor;
    lastCategoryColor.current = categoryColor;
    if (!isWineColor(categoryColor)) return;
    if (value.color === categoryColor) return;
    if (!value.color || changed) onChange({ ...value, color: categoryColor });
  }, [categoryColor, value, onChange]);

  const labels = world ? WINE_FIELD_LABELS[world] : null;
  const preview =
    world && (value.producer?.trim() || value.region?.trim())
      ? structuredWineDisplay({
          world,
          vintage: value.vintage ?? null,
          producer: value.producer,
          region: value.region,
          vineyard: value.vineyard,
          varietal: showsVarietal(world) ? value.varietal : null,
          cuvee: value.cuvee,
        })
      : null;

  return (
    <div className="space-y-4">
      {worldScope === 'both' && (
        <fieldset>
          <legend className="mb-1 block text-sm text-cream-200">
            {forSomeoneElse ? 'Old World or New World?' : 'Is your wine Old World or New World?'}
          </legend>
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {WINE_WORLDS.map((w) => (
              <button
                key={w}
                type="button"
                role="radio"
                aria-checked={value.world === w}
                disabled={disabled}
                onClick={() =>
                  set(
                    w === 'old'
                      ? { world: w, varietal: undefined, varietalId: undefined }
                      : { world: w },
                  )
                }
                className={`rounded-md border px-3 py-3 text-sm transition ${
                  value.world === w
                    ? 'border-gold-400 bg-gold-600/15 text-gold-200'
                    : 'border-ink-800 text-cream-200 hover:border-gold-600/50'
                }`}
              >
                {WINE_WORLD_LABELS[w]}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-cream-300/50">
            Old World is Europe (Burgundy, Bordeaux, Rioja…); New World is everywhere else
            (California, Australia, Chile…).
          </p>
        </fieldset>
      )}

      {world && labels && (
        <>
          <div>
            <span className="mb-1 block text-sm text-cream-200">
              {labels.vintage}
              <span className="text-wine-300"> *</span>
            </span>
            <div className="flex items-center gap-4">
              <input
                type="number"
                inputMode="numeric"
                aria-label={labels.vintage}
                min={1800}
                max={2100}
                className="field w-32"
                placeholder="2019"
                disabled={disabled || value.vintage === null}
                value={value.vintage ?? ''}
                onChange={(e) =>
                  set({ vintage: e.target.value ? Number(e.target.value) : undefined })
                }
              />
              <label className="flex items-center gap-2 text-sm text-cream-200">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-gold-500"
                  checked={value.vintage === null}
                  disabled={disabled}
                  onChange={(e) => set({ vintage: e.target.checked ? null : undefined })}
                />
                NV (non-vintage)
              </label>
            </div>
          </div>

          <LookupTypeahead
            kind="producers"
            label={labels.producer}
            required
            placeholder={world === 'old' ? 'Joseph Drouhin' : 'Morgan'}
            value={{ id: value.producerId, name: value.producer ?? '' }}
            onChange={(v) => set({ producerId: v.id, producer: v.name })}
            disabled={disabled}
          />
          <LookupTypeahead
            kind="regions"
            label={labels.region}
            required
            world={world}
            placeholder={world === 'old' ? 'Meursault' : 'Santa Lucia Highlands'}
            value={{ id: value.regionId, name: value.region ?? '' }}
            onChange={(v) => set({ regionId: v.id, region: v.name })}
            disabled={disabled}
          />
          <LookupTypeahead
            kind="vineyards"
            label={labels.vineyard}
            regionId={value.regionId}
            placeholder={world === 'old' ? 'Les Charmes' : 'Double L'}
            value={{ id: value.vineyardId, name: value.vineyard ?? '' }}
            onChange={(v) => set({ vineyardId: v.id, vineyard: v.name })}
            disabled={disabled}
          />
          {showsVarietal(world) && labels.varietal && (
            <LookupTypeahead
              kind="varietals"
              label={labels.varietal}
              world={world}
              placeholder="Chardonnay"
              createExtras={value.color ? { color: value.color } : undefined}
              value={{ id: value.varietalId, name: value.varietal ?? '' }}
              onChange={(v) => set({ varietalId: v.id, varietal: v.name })}
              disabled={disabled}
            />
          )}
          <label className="block">
            <span className="mb-1 block text-sm text-cream-200">{labels.cuvee}</span>
            <input
              className="field"
              maxLength={200}
              placeholder={world === 'old' ? 'Clos des Mouches' : 'Insignia'}
              value={value.cuvee ?? ''}
              disabled={disabled}
              onChange={(e) => set({ cuvee: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm text-cream-200">
              Colour<span className="text-wine-300"> *</span>
            </span>
            <select
              className="field"
              value={value.color ?? ''}
              disabled={disabled}
              onChange={(e) =>
                set({ color: isWineColor(e.target.value) ? e.target.value : undefined })
              }
            >
              <option value="">Choose…</option>
              {WINE_COLORS.map((c) => (
                <option key={c} value={c}>
                  {WINE_COLOR_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          {preview && (
            <p className="rounded-md border border-ink-800 bg-ink-950/60 px-3 py-2 text-sm text-cream-100">
              <span className="mr-2 text-xs uppercase tracking-wide text-gold-400">Shows as</span>
              {preview}
            </p>
          )}
        </>
      )}
    </div>
  );
}
